import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Instagram, Facebook, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Footer({
  onOpenNewsletter
}: {
  onOpenNewsletter?: () => void;
}) {
  const footerLinks = {
    Company: [{
      name: 'About',
      href: '/company/about'
    }, {
      name: 'Board',
      href: '/company/board'
    }, {
      name: 'Team',
      href: '/company/team'
    }, {
      name: 'Partners',
      href: '/company/partners'
    }, {
      name: 'Careers & Culture',
      href: '/company/careers'
    }, {
      name: 'Contact Us',
      href: '/company/contact'
    }],
    'Our Concepts': [{
      name: 'Restaurants',
      href: '/concepts/restaurants'
    }, {
      name: 'Private Dining & Events',
      href: '/concepts/private-dining'
    }, {
      name: 'Grab & Go',
      href: '/concepts/grab-and-go'
    }, {
      name: 'Commissary & Catering',
      href: '/concepts/catering'
    }, {
      name: 'Ready-to-Cook',
      href: '/concepts/ready-to-cook'
    }],
    'Advisory & Investment': [{
      name: 'Investment & Growth',
      href: '/advisory/investment'
    }, {
      name: 'Real Estate',
      href: '/advisory/real-estate'
    }, {
      name: 'Management Company',
      href: '/advisory/management'
    }],
    Memberships: [{
      name: 'Personal',
      href: '/memberships/personal'
    }, {
      name: 'Corporate',
      href: '/memberships/corporate'
    }],
    'Press / Media': [{
      name: 'Featured Highlights',
      href: '/press'
    }, {
      name: 'Announcements',
      href: '/press'
    }, {
      name: 'Awards & Recognition',
      href: '/press'
    }]
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <footer className="bg-brand-dark pt-32 pb-12 border-t border-white/10 relative overflow-hidden" data-id="element-97">
      {/* Noise Overlay */}
      <div className="absolute inset-0 bg-noise pointer-events-none z-20 opacity-30" data-id="element-98"></div>

      {/* Large Logo Watermark */}
      <div className="absolute -bottom-40 -right-40 opacity-[0.03] pointer-events-none z-0" data-id="element-99">
        <img src="https://cdn.magicpatterns.com/uploads/cPkLq24FKKtQpiCoiqri7q/Ресурс_1.svg" alt="" className="w-[800px] h-[800px] filter invert" data-id="element-100" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-101">
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-16 mb-32" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} data-id="element-102">
          <motion.div variants={itemVariants} className="lg:col-span-2" data-id="element-103">
            <img src="https://cdn.magicpatterns.com/uploads/cPkLq24FKKtQpiCoiqri7q/Ресурс_1.svg" alt="Jibek Jolu Group Logo" className="h-20 filter invert mb-10 opacity-90" data-id="element-104" />
            <p className="text-white/70 font-light leading-relaxed mb-10 max-w-sm text-lg" data-id="element-105">
              Honest food, warm hospitality, and a deep sense of community since
              2009.
            </p>

            <div className="mb-10" data-id="element-106">
              <h4 className="text-white font-serif mb-6 text-xl" data-id="element-107">
                Subscribe to our Newsletter
              </h4>
              <button onClick={onOpenNewsletter} className="border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-dark px-8 py-3 text-sm uppercase tracking-[0.2em] transition-colors duration-300" data-id="element-108">
                Join The Circle
              </button>
            </div>

            <div className="flex gap-6" data-id="element-109">
              {[{
              Icon: Instagram,
              url: 'https://instagram.com'
            }, {
              Icon: Facebook,
              url: 'https://facebook.com'
            }, {
              Icon: Linkedin,
              url: 'https://linkedin.com'
            }].map(({
              Icon,
              url
            }, idx) => <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:bg-brand-gold hover:border-brand-gold hover:text-brand-dark transition-all hover:scale-110" data-id="element-110">
                  <Icon size={18} data-id="element-111" />
                </a>)}
            </div>
          </motion.div>

          {Object.entries(footerLinks).map(([category, links], idx) => <motion.div variants={itemVariants} key={idx} className="lg:col-span-1" data-id="element-112">
              <h4 className="text-white font-serif mb-8 text-xl" data-id="element-113">{category}</h4>
              <ul className="space-y-4" data-id="element-114">
                {links.map((link, linkIdx) => <li key={linkIdx} data-id="element-115">
                    <Link to={link.href} className="text-white/50 hover:text-brand-gold text-sm font-light transition-colors inline-block hover:translate-x-1 transform duration-300" data-id="element-116">
                      {link.name}
                    </Link>
                  </li>)}
              </ul>
            </motion.div>)}
        </motion.div>

        <motion.div className="flex flex-col md:flex-row items-center justify-between pt-10 border-t border-white/10 text-white/40 text-xs font-light tracking-wider" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8,
        duration: 0.8
      }} data-id="element-117">
          <p data-id="element-118">Jibek Jolu Group © 2009-2026. All Rights Reserved.</p>
          <div className="flex items-center gap-8 mt-6 md:mt-0" data-id="element-119">
            <Link to="/company/contact" className="hover:text-white transition-colors" data-id="element-120">
              Privacy Policy
            </Link>
            <Link to="/company/contact" className="hover:text-white transition-colors" data-id="element-121">
              Terms of Service
            </Link>
            <button onClick={scrollToTop} className="flex items-center gap-2 text-brand-gold hover:text-white transition-colors ml-4" data-id="element-122">
              BACK TO TOP <ArrowUp size={14} data-id="element-123" />
            </button>
          </div>
        </motion.div>
      </div>
    </footer>;
}